import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { throwIfNotOk } from "@/lib/api-error";
import type { Therapist, TherapySession } from "@/types";
import type { EnrollmentQuestionnaireResponse } from "@/types/enrollment-questionnaire";
import {
  TherapistDocumentsSection,
  TherapistDetailSkeleton,
  TherapistFlagsCard,
  TherapistQuestionnaireSection,
  TherapistServicesSection,
} from "@/components/admin/therapists";
import {
  Panel,
  RatingDistribution,
  TherapistBillingPanel,
  TherapistBioPanel,
  TherapistCommandBar,
  TherapistIdentityPanel,
  TherapistSessionsTable,
  TherapistTimelinePanel,
  TherapistTimeOffPanel,
  TherapistVitals,
  computeTherapistVitals,
} from "@/components/admin/therapists/detail";
import { MatchingProfileEditor } from "@/components/matching/MatchingProfileEditor";

export const Route = createFileRoute(
  "/dashboard/admin/therapists/$therapistId"
)({
  component: TherapistDetail,
});

type AdminTherapist = Therapist & {
  questionnaireResponse?: EnrollmentQuestionnaireResponse | null;
};

function TherapistDetail() {
  const { therapistId } = Route.useParams();

  const { data: therapist, isLoading } = useQuery({
    queryKey: ["admin", "therapist", therapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${therapistId}`);
      if (!res.ok) throw new Error("Not found");
      return res.json() as Promise<AdminTherapist>;
    },
  });

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["admin", "therapist-sessions", therapistId],
    queryFn: async () => {
      const res = await fetch(`/api/therapists/${therapistId}/sessions`);
      await throwIfNotOk(res, "Greška pri učitavanju podataka");
      return res.json() as Promise<TherapySession[]>;
    },
  });

  const vitals = useMemo(
    () => (therapist ? computeTherapistVitals(therapist, sessions) : null),
    [therapist, sessions]
  );

  if (isLoading) return <TherapistDetailSkeleton />;
  if (!therapist) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg text-muted-foreground">Terapeut nije pronađen.</p>
        <Link
          to="/dashboard/admin/therapists"
          className="mt-4 text-sm font-medium text-primary hover:underline"
        >
          Nazad na listu
        </Link>
      </div>
    );
  }

  // An application under review has no history to analyse, so the page leads
  // with the evidence the decision rests on instead of with performance.
  const underReview = therapist.status === "pending";

  return (
    <div className="pb-10">
      <TherapistCommandBar therapist={therapist} />

      <div className="mt-4 space-y-4">
        {!underReview && vitals && <TherapistVitals vitals={vitals.vitals} />}

        <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,21rem)_minmax(0,1fr)]">
          <div className="space-y-4">
            <TherapistIdentityPanel therapist={therapist} />

            {!underReview && vitals && (
              <Panel title="Raspodjela ocjena">
                <RatingDistribution
                  distribution={vitals.ratingDistribution}
                  total={vitals.ratedCount}
                  average={vitals.avgRating}
                />
              </Panel>
            )}

            {!underReview && <TherapistTimeOffPanel therapistId={therapist.id} />}

            <TherapistDocumentsSection
              therapistId={therapist.id}
              highlightMissing={underReview}
            />
            <TherapistBillingPanel therapist={therapist} />
            <TherapistFlagsCard therapist={therapist} />
            <TherapistTimelinePanel therapist={therapist} />
          </div>

          <div className="space-y-4">
            {!underReview && (
              <TherapistSessionsTable
                sessions={sessions}
                isLoading={sessionsLoading}
                therapistUserId={therapist.userId}
              />
            )}
            <TherapistBioPanel bio={therapist.bio} />
            <TherapistQuestionnaireSection
              response={therapist.questionnaireResponse ?? null}
            />
            <TherapistServicesSection therapistId={therapist.id} />
            <Panel title="Matching profil">
              <MatchingProfileEditor
                bare
                dense
                endpoint={`/api/admin/therapists/${therapist.id}/matching-profile`}
                queryKey={["admin", "therapist", therapist.id, "matching-profile"]}
              />
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
}
